import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { localDateFromYMD } from '../utils/dbTime';
// __EQUIP_COMPAT__ show what the driver's unit can actually carry (derived), not the old manual number
import { unitCapacity, fmtLb } from '../utils/equipmentCompat';
import { getDefaultUnitSystem } from '../utils/units';
import { TRUCK_COLS, TRAILER_COLS, invalidateEquipmentCache, fetchRules, updateRules } from '../services/equipmentService';
import { driverCanSelfUnload } from '../utils/equipmentCompat';   // __SELF_UNLOAD__
import { setDriverSelfUnload } from '../utils/equipmentDowntime';
import { runAssignOps } from '../services/equipmentAssignService';   // __EQUIP_ASSIGN__

function DriverManagementModal({ onClose, onSuccess }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [combinations, setCombinations] = useState([]);
  const [equipSavingId, setEquipSavingId] = useState(null);
  // __SELF_UNLOAD__ (2026-10-08) "Can self-unload (operate on-site equipment)" per driver (Settings > Equipment
  // rules, driver_caps). Jobs booked "self-unload: on-site equipment" warn for a driver without it, and the
  // hub's booking availability only counts drivers who have it for those jobs.
  const [eqRules, setEqRules] = useState(null);
  const [capSavingId, setCapSavingId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    license_number: '',
    license_expiry: '',
    license_class: '',
    status: 'available',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: '',
    work_start_time: '',
    work_end_time: '',
    max_daily_hours: '',
    hours_strict: false,
    is_available: true
  });

  useEffect(() => {
    fetchDrivers();
    fetchCombinations();
    fetchRules(true).then(setEqRules).catch(() => setEqRules(null));
  }, []);

  const setSelfUnload = async (driver, can) => {
    setCapSavingId(driver.id);
    setError(null);
    try {
      setEqRules(await updateRules((r) => setDriverSelfUnload(r, driver.id, can)));
      invalidateEquipmentCache();
    } catch (err) {
      setError('Could not save "Can self-unload": ' + (err.message || err));
    } finally {
      setCapSavingId(null);
    }
  };

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select(`
          id,
          name,
          email,
          phone,
          license_number,
          license_expiry,
          license_class,
          status,
          emergency_contact_name,
          emergency_contact_phone,
          notes,
          is_available,
          current_truck_id,
          user_id,
          created_at,
          user:user_id(id, name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDrivers(data || []);
    } catch (err) {
      console.error('Error fetching drivers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCombinations = async () => {
    try {
      const { data, error } = await supabase
        .from('truck_trailer_combinations')
        .select(`id, combination_name, default_driver_id, combined_max_weight_kg, truck:trucks(${TRUCK_COLS}), trailer:trailers(${TRAILER_COLS})`)
        .order('combination_name', { ascending: true });
      if (error) throw error;
      setCombinations(data || []);
    } catch (err) {
      console.error('Error fetching combinations:', err);
    }
  };

  const comboForDriver = (driverId) =>
    combinations.find((c) => c.default_driver_id === driverId) || null;

  // __EQUIP_ASSIGN__ picking a combination makes its truck the driver's PRIMARY setup (its trailer
  // hitched to it) through equipment_assign() - the same audited path as the Equipment
  // Assignment view, which also handles shared units and dates. '' clears his primary.
  const assignEquipment = async (driver, comboId) => {
    setEquipSavingId(driver.id);
    setError(null);
    try {
      const cur = comboForDriver(driver.id);
      const combo = combinations.find((c) => c.id === comboId) || null;
      const truckId = combo && combo.truck ? combo.truck.id : null;
      if (comboId && !truckId) throw new Error('That combination has no truck.');
      const ops = [];
      if (truckId) {
        ops.push({ op: 'assign', driver_id: driver.id, truck_id: truckId, primary: true });
        if (combo.trailer && combo.trailer.id) ops.push({ op: 'couple', truck_id: truckId, trailer_id: combo.trailer.id });
      } else if (cur && cur.truck) {
        ops.push({ op: 'unassign', driver_id: driver.id, truck_id: cur.truck.id });
      }
      await runAssignOps(ops);
      invalidateEquipmentCache();
      await fetchCombinations();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error assigning equipment:', err);
      setError(err.message);
    } finally {
      setEquipSavingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      license_number: '',
      license_expiry: '',
      license_class: '',
      status: 'available',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      notes: '',
      work_start_time: '',
      work_end_time: '',
      max_daily_hours: '',
      hours_strict: false,
      is_available: true
    });
    setEditingDriver(null);
    setError(null);
  };

  const handleEdit = (driver) => {
    setFormData({
      name: driver.name || driver.user?.name || '',
      email: driver.email || driver.user?.email || '',
      phone: driver.phone || '',
      license_number: driver.license_number || '',
      license_expiry: driver.license_expiry || '',
      license_class: driver.license_class || '',
      status: driver.status || 'available',
      emergency_contact_name: driver.emergency_contact_name || '',
      emergency_contact_phone: driver.emergency_contact_phone || '',
      notes: driver.notes || '',
      work_start_time: driver.work_start_time || '',
      work_end_time: driver.work_end_time || '',
      max_daily_hours: driver.max_daily_hours != null ? String(driver.max_daily_hours) : '',
      hours_strict: !!driver.hours_strict,
      is_available: driver.is_available !== false
    });
    setEditingDriver(driver);
    setShowForm(true);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Driver name is required');
      }

      const driverData = {
        name: formData.name || null,
        email: formData.email || null,
        phone: formData.phone || null,
        license_number: formData.license_number || null,
        license_expiry: formData.license_expiry || null,
        license_class: formData.license_class || null,
        status: formData.status,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        notes: formData.notes || null,
        work_start_time: formData.work_start_time || null,
        work_end_time: formData.work_end_time || null,
        max_daily_hours: formData.max_daily_hours !== '' && formData.max_daily_hours != null ? Number(formData.max_daily_hours) : null,
        hours_strict: !!formData.hours_strict,
        is_available: formData.is_available !== false
      };

      // Resilience: the hours-of-operation columns are added by driver_hours.sql.
      // If that migration hasn't been run yet, an insert/update including those
      // fields would fail on unknown columns and block a core dispatch action, so
      // on that specific failure we retry without them (driver still saves).
      const HOURS_FIELDS = ['work_start_time', 'work_end_time', 'max_daily_hours', 'hours_strict'];
      const isMissingHoursColumn = (err) => {
        const m = ((err && (err.message || err.details)) || '').toLowerCase();
        return HOURS_FIELDS.some((f) => m.includes(f)) &&
          (m.includes('column') || m.includes('schema cache') || m.includes('could not find'));
      };
      const saveDriver = async (payload) => {
        if (editingDriver) {
          return supabase.from('drivers').update(payload).eq('id', editingDriver.id);
        }
        return supabase.from('drivers').insert([payload]);
      };

      let { error } = await saveDriver(driverData);
      if (error && isMissingHoursColumn(error)) {
        const fallback = { ...driverData };
        HOURS_FIELDS.forEach((f) => delete fallback[f]);
        console.warn('drivers hours-of-operation columns not present yet; saving without them (run driver_hours.sql)');
        ({ error } = await saveDriver(fallback));
      }
      if (error) throw error;

      // Refresh driver list
      await fetchDrivers();
      resetForm();
      setShowForm(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error saving driver:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // __DRIVER_DEACTIVATE__ (2026-09-12) Retiring a driver is a SOFT action: is_available=false
  // hides them from every assignment dropdown (the compat service / archive filter on it)
  // while all history (loads, GPS, payroll, reports) stays intact and it's reversible.
  const setActive = async (driver, active) => {
    setError(null);
    setEquipSavingId(driver.id);
    try {
      const { error } = await supabase
        .from('drivers')
        .update({ is_available: !!active })
        .eq('id', driver.id);
      if (error) throw error;
      await fetchDrivers();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error updating driver active flag:', err);
      setError(err.message);
    } finally {
      setEquipSavingId(null);
    }
  };

  // Hard delete is only allowed for a driver with NO load history: loads.driver_id is
  // ON DELETE NO ACTION (as are gps_locations, routes, reports...), so deleting a used
  // driver would fail with a raw FK error. We check the main one up front and explain.
  const handleDelete = async (driver) => {
    if (deleteConfirmId !== driver.id) { setDeleteConfirmId(driver.id); return; }
    setDeleteConfirmId(null);

    try {
      const { count, error: countError } = await supabase
        .from('loads')
        .select('id', { count: 'exact', head: true })
        .eq('driver_id', driver.id);
      if (countError) throw countError;
      if ((count || 0) > 0) {
        setError('This driver has ' + count + ' load' + (count === 1 ? '' : 's') + ' in history and cannot be deleted. Keep them deactivated instead - the history stays intact.');
        return;
      }
      const { error } = await supabase
        .from('drivers')
        .delete()
        .eq('id', driver.id);

      if (error) throw error;
      await fetchDrivers();
    } catch (err) {
      console.error('Error deleting driver:', err);
      // loads is only the FIRST of nine tables referencing drivers with ON DELETE
      // NO ACTION (gps_locations, fuel_stops, routes, pre_trip_reports,
      // geofence_events, geofence_state, driver_breaks, load_comments). A driver
      // with no loads but a single GPS ping still fails here, and the raw Postgres
      // text names an internal constraint rather than telling the user what to do.
      const fk = err && (err.code === '23503' || /foreign key|violates/i.test(err.message || ''));
      setError(fk
        ? 'This driver still has records elsewhere in the system (GPS history, reports, routes or comments) and cannot be deleted. Deactivate them instead - the history stays intact and it is reversible.'
        : err.message);
    }
  };

  const filteredDrivers = drivers.filter(d => {
    const name = d.name || d.user?.name || '';
    const email = d.email || d.user?.email || '';
    const phone = d.phone || '';
    return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           email.toLowerCase().includes(searchTerm.toLowerCase()) ||
           phone.includes(searchTerm);
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'var(--color-success)';
      case 'on_duty': return 'var(--accent-blue)';
      case 'off_duty': return 'var(--text-secondary)';
      case 'on_break': return 'var(--color-warning)';
      case 'unavailable': return 'var(--color-danger)';
      default: return 'var(--text-muted)';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return 'Available';
      case 'on_duty': return 'On Duty';
      case 'off_duty': return 'Off Duty';
      case 'on_break': return 'On Break';
      case 'unavailable': return 'Unavailable';
      default: return status;
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: 'var(--surface-2)',
    border: '1px solid var(--border-default)',
    borderRadius: '5px',
    color: 'var(--text-primary)',
    fontSize: '1rem'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: 'var(--text-secondary)',
    fontSize: '0.9rem'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--surface-1)',
        borderRadius: '10px',
        padding: '2rem',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid var(--surface-2)'
        }}>
          <h2 style={{ color: 'var(--accent-primary)', margin: 0 }}>
            {showForm ? (editingDriver ? 'Edit Driver' : 'Add Driver') : 'Manage Drivers'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            x
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            backgroundColor: 'var(--color-danger)',
            color: 'var(--text-primary)',
            padding: '1rem',
            borderRadius: '5px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {showForm ? (
          /* Driver Form */
          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              {/* Name */}
              <div>
                <label style={labelStyle}>Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={inputStyle}
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={inputStyle}
                />
              </div>

              {/* Phone */}
              <div>
                <label style={labelStyle}>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  style={inputStyle}
                />
              </div>

              {/* Status */}
              <div>
                <label style={labelStyle}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={inputStyle}
                >
                  <option value="available">Available</option>
                  <option value="on_duty">On Duty</option>
                  <option value="off_duty">Off Duty</option>
                  <option value="on_break">On Break</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </div>

              {/* Hours of Operation (used by the load-fit / scheduling analysis) */}
              <div>
                <label style={labelStyle}>Works From</label>
                <input
                  type="time"
                  value={formData.work_start_time}
                  onChange={(e) => setFormData({ ...formData, work_start_time: e.target.value })}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Works Until</label>
                <input
                  type="time"
                  value={formData.work_end_time}
                  onChange={(e) => setFormData({ ...formData, work_end_time: e.target.value })}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Max Daily Hours</label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  step="0.5"
                  placeholder="10"
                  value={formData.max_daily_hours}
                  onChange={(e) => setFormData({ ...formData, max_daily_hours: e.target.value })}
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="drvHoursStrict"
                  checked={formData.hours_strict}
                  onChange={(e) => setFormData({ ...formData, hours_strict: e.target.checked })}
                />
                <label htmlFor="drvHoursStrict" style={{ ...labelStyle, margin: 0 }}>
                  Strict hours (escalate jobs outside this window)
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="drvActive"
                  checked={formData.is_available !== false}
                  onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })}
                />
                <label htmlFor="drvActive" style={{ ...labelStyle, margin: 0 }}>
                  Active (available for assignment)
                </label>
              </div>

              {/* License Number */}
              <div>
                <label style={labelStyle}>License Number</label>
                <input
                  type="text"
                  value={formData.license_number}
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                  style={inputStyle}
                />
              </div>

              {/* License Class */}
              <div>
                <label style={labelStyle}>License Class</label>
                <select
                  value={formData.license_class}
                  onChange={(e) => setFormData({ ...formData, license_class: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">Select class...</option>
                  <option value="A">Class A (AZ)</option>
                  <option value="B">Class B (BZ)</option>
                  <option value="C">Class C (CZ)</option>
                  <option value="D">Class D (DZ)</option>
                  <option value="G">Class G</option>
                </select>
              </div>

              {/* License Expiry */}
              <div>
                <label style={labelStyle}>License Expiry</label>
                <input
                  type="date"
                  value={formData.license_expiry}
                  onChange={(e) => setFormData({ ...formData, license_expiry: e.target.value })}
                  style={inputStyle}
                />
              </div>

              {/* Spacer */}
              <div></div>

              {/* Emergency Contact Name */}
              <div>
                <label style={labelStyle}>Emergency Contact Name</label>
                <input
                  type="text"
                  value={formData.emergency_contact_name}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                  style={inputStyle}
                />
              </div>

              {/* Emergency Contact Phone */}
              <div>
                <label style={labelStyle}>Emergency Contact Phone</label>
                <input
                  type="tel"
                  value={formData.emergency_contact_phone}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                  style={inputStyle}
                />
              </div>

              {/* Notes */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                  placeholder="Any additional notes about this driver..."
                />
              </div>
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => { resetForm(); setShowForm(false); }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--surface-3)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: saving ? 'var(--surface-3)' : 'var(--accent-primary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {saving ? 'Saving...' : (editingDriver ? 'Update Driver' : 'Add Driver')}
              </button>
            </div>
          </form>
        ) : (
          /* Driver List */
          <>
            {/* Actions Bar */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <input
                type="text"
                placeholder="Search drivers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--accent-primary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}
              >
                + Add Driver
              </button>
            </div>

            {/* Driver List */}
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                Loading drivers...
              </div>
            ) : filteredDrivers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                {searchTerm ? 'No drivers match your search' : 'No drivers yet. Add one to get started!'}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '0.75rem',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {filteredDrivers.map((driver) => {
                  const driverName = driver.name || driver.user?.name || 'Unknown';
                  const driverEmail = driver.email || driver.user?.email;
                  
                  return (
                    <div
                      key={driver.id}
                      style={{
                        backgroundColor: 'var(--surface-2)',
                        padding: '1rem',
                        borderRadius: '5px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '1.1rem' }}>
                            {driverName}
                          </span>
                          <span style={{
                            padding: '0.2rem 0.5rem',
                            borderRadius: '3px',
                            fontSize: '0.75rem',
                            backgroundColor: getStatusColor(driver.status),
                            color: 'var(--text-primary)'
                          }}>
                            {getStatusLabel(driver.status)}
                          </span>
                          {driver.is_available === false && (
                            <span style={{ padding: '0.2rem 0.5rem', borderRadius: '3px', fontSize: '0.75rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-muted)', border: '1px solid var(--border-default)' }}>INACTIVE</span>
                          )}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                          {driver.phone && <span>{driver.phone}</span>}
                          {driver.phone && driverEmail && <span> | </span>}
                          {driverEmail && <span>{driverEmail}</span>}
                        </div>
                        {driver.license_class && (
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            License: Class {driver.license_class}
                            {driver.license_expiry && localDateFromYMD(driver.license_expiry) && ` (Expires: ${localDateFromYMD(driver.license_expiry).toLocaleDateString()})`}
                          </div>
                        )}
                        {(() => {
                          const assigned = comboForDriver(driver.id);
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                              <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Equipment:</span>
                              <select
                                value={assigned ? assigned.id : ''}
                                disabled={equipSavingId === driver.id}
                                onChange={(e) => assignEquipment(driver, e.target.value)}
                                style={{ padding: '0.3rem 0.5rem', backgroundColor: 'var(--surface-1)', border: '1px solid var(--border-default)', borderRadius: '4px', color: 'var(--text-primary)', fontSize: '0.8rem', maxWidth: '240px' }}
                              >
                                <option value="">— none —</option>
                                {combinations.map((c) => (
                                  <option key={c.id} value={c.id}>{c.combination_name}</option>
                                ))}
                              </select>
                              {equipSavingId === driver.id && <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>saving…</span>}
                              {assigned && (() => {
                                const cap = assigned.truck ? unitCapacity({ truck: assigned.truck, trailer: assigned.trailer || null, combo: assigned }) : null;
                                return (
                                  <span style={{ color: cap && cap.payloadLb != null ? 'var(--text-muted)' : 'var(--color-warning)', fontSize: '0.75rem' }}>
                                    {cap && cap.payloadLb != null ? 'payload ' + fmtLb(cap.payloadLb, getDefaultUnitSystem()) : 'payload unknown - fill in the truck/trailer'}
                                  </span>
                                );
                              })()}
                              {!assigned && !driver.current_truck_id && <span style={{ color: 'var(--color-warning)', fontSize: '0.75rem' }}>no unit - every assignment shows a "no unit" warning</span>}
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>(primary setup - shared units and dates: Equipment Assignment view)</span>
                            </div>
                          );
                        })()}
                        <label data-self-unload-cap style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.4rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                          <input type="checkbox" disabled={!eqRules || capSavingId === driver.id}
                            checked={!!eqRules && driverCanSelfUnload(eqRules, driver.id)}
                            onChange={(e) => setSelfUnload(driver, e.target.checked)} />
                          Can self-unload (operate on-site equipment)
                          {capSavingId === driver.id && <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>saving…</span>}
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleEdit(driver)}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: 'var(--accent-blue)',
                            color: 'var(--text-primary)',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          Edit
                        </button>
                        {driver.is_available !== false ? (
                          <button onClick={() => setActive(driver, false)} disabled={equipSavingId === driver.id} title="Hide from assignment; keeps all history" style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-warning)', color: 'var(--bg-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold' }}>Deactivate</button>
                        ) : (
                          <>
                            <button onClick={() => setActive(driver, true)} disabled={equipSavingId === driver.id} style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-success)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem' }}>Reactivate</button>
                            {deleteConfirmId === driver.id ? (
                              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Sure?</span>
                                <button onClick={() => handleDelete(driver)} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Yes</button>
                                <button onClick={() => setDeleteConfirmId(null)} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>No</button>
                              </div>
                            ) : (
                              <button onClick={() => setDeleteConfirmId(driver.id)} title="Only allowed for a driver with no load history" style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem' }}>Delete</button>
                            )}
                          </>
                        )}  
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Summary */}
            <div style={{
              marginTop: '1rem',
              paddingTop: '1rem',
              borderTop: '1px solid var(--surface-2)',
              color: 'var(--text-muted)',
              fontSize: '0.9rem',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span>
                {filteredDrivers.length} driver{filteredDrivers.length !== 1 ? 's' : ''} 
                {searchTerm && ` matching "${searchTerm}"`}
              </span>
              <span>
                {drivers.filter(d => d.status === 'available').length} available | 
                {drivers.filter(d => d.status === 'on_duty').length} on duty | 
                {drivers.filter(d => d.status === 'off_duty').length} off duty
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DriverManagementModal;
